import React, { useEffect, useMemo, useState } from 'react';
import { Container, Button, Form, Alert, Pagination, Placeholder, Row, Col, Modal } from 'react-bootstrap';
import { getGLeaders, createGLeader, updateGLeader, deleteGLeader } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

// Custom styles for search highlighting
const customStyles = `
  .modal-600w {
    max-width: 650px !important;
  }

  .search-highlight {
    background-color: #fff3cd !important;
    color: #856404 !important;
    padding: 0.1em 0.2em !important;
    border-radius: 0.2em !important;
    font-weight: 500 !important;
  }

  [data-theme="dark"] .search-highlight {
    background-color: #664d03 !important;
    color: #ffda6a !important;
  }

  .theme-card {
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    box-shadow: 0 0.35rem 1rem rgba(15, 23, 42, 0.05);
    transition: background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
    position: relative;
    overflow: hidden;
  }

  .theme-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 0.6rem 1.45rem rgba(15, 23, 42, 0.1);
  }

  .theme-card-draggable {
    cursor: grab;
  }

  .theme-card-draggable:active {
    cursor: grabbing;
  }

  .theme-card-dragging {
    opacity: 0.85;
  }

  .theme-card-pinned {
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.16), rgba(13, 110, 253, 0.05));
    border-color: rgba(13, 110, 253, 0.45) !important;
    box-shadow: 0 0.95rem 2.2rem rgba(13, 110, 253, 0.28);
  }

  .theme-card-pinned::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 2px solid rgba(13, 110, 253, 0.35);
    pointer-events: none;
    opacity: 0.85;
  }

  [data-theme="dark"] .theme-card {
    box-shadow: 0 0.45rem 1.1rem rgba(0, 0, 0, 0.4);
  }

  [data-theme="dark"] .theme-card:hover {
    box-shadow: 0 0.7rem 1.7rem rgba(0, 0, 0, 0.55);
  }

  [data-theme="dark"] .theme-card-pinned {
    background: linear-gradient(135deg, rgba(46, 59, 92, 0.82), rgba(110, 168, 254, 0.28));
    border-color: rgba(110, 168, 254, 0.55) !important;
    box-shadow: 0 1.05rem 2.45rem rgba(13, 110, 253, 0.35);
  }

  [data-theme="dark"] .theme-card-pinned::after {
    border-color: rgba(110, 168, 254, 0.5);
    opacity: 0.9;
  }

  .theme-card-drag-over {
    border-color: var(--bs-primary) !important;
    box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.25);
  }

  .theme-card-drag-over::after {
    border-color: transparent;
  }

`;

const sortThemeItems = (list) => {
  return [...list].sort((a, b) => {
    const aPinned = a.pinned ? 1 : 0;
    const bPinned = b.pinned ? 1 : 0;
    if (aPinned !== bPinned) {
      return bPinned - aPinned;
    }
    const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
    const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
    return dateB - dateA;
  });
};

const THEME_ORDER_STORAGE_KEY = 'gleaderOrder';

const getStoredOrder = () => {
  try {
    const raw = localStorage.getItem(THEME_ORDER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((value) => String(value));
  } catch (error) {
    return [];
  }
};

const persistOrder = (list) => {
  try {
    const ids = list.map((item) => item.id);
    localStorage.setItem(THEME_ORDER_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    // Silently ignore storage errors (quota, disabled, etc.)
  }
};

const removeFromStoredOrder = (idToRemove) => {
  try {
    const storedOrder = getStoredOrder();
    if (!storedOrder.length) return;
    const filtered = storedOrder.filter((storedId) => storedId !== String(idToRemove));
    localStorage.setItem(THEME_ORDER_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    // Ignore storage errors
  }
};

const applyStoredOrder = (list) => {
  const storedOrder = getStoredOrder();
  if (!storedOrder.length) return list;

  const mappedItems = new Map(list.map((item) => [String(item.id), item]));
  const seen = new Set();
  const orderedPinned = [];
  const orderedUnpinned = [];

  for (const id of storedOrder) {
    const item = mappedItems.get(String(id));
    if (!item) continue;
    seen.add(String(id));
    if (item.pinned) {
      orderedPinned.push(item);
    } else {
      orderedUnpinned.push(item);
    }
  }

  const newPinned = [];
  const newUnpinned = [];

  for (const item of list) {
    const key = String(item.id);
    if (seen.has(key)) continue;
    if (item.pinned) {
      newPinned.push(item);
    } else {
      newUnpinned.push(item);
    }
  }

  return [...orderedPinned, ...newPinned, ...newUnpinned, ...orderedUnpinned];
};

const reorderAfterPinChange = (list, updatedItem) => {
  const updatedList = list.map((item) =>
    item.id === updatedItem.id ? { ...item, ...updatedItem } : item
  );
  const target = updatedList.find((item) => item.id === updatedItem.id);
  if (!target) {
    return updatedList;
  }

  if (target.pinned) {
    // Keep existing pinned order and append the newly pinned theme to the end of pinned items.
    const pinnedItems = [];
    const unpinnedItems = [];

    updatedList.forEach((item) => {
      if (item.id === target.id) return;
      if (item.pinned) {
        pinnedItems.push(item);
      } else {
        unpinnedItems.push(item);
      }
    });

    pinnedItems.push(target);
    return [...pinnedItems, ...unpinnedItems];
  }

  // When unpinning, preserve the current order of remaining pinned items and unpinned items.
  const pinnedItems = [];
  const unpinnedItems = [];

  updatedList.forEach((item) => {
    if (item.pinned) {
      pinnedItems.push(item);
    } else {
      unpinnedItems.push(item);
    }
  });

  return [...pinnedItems, ...unpinnedItems];
};

function GLeaders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingMarketSentiment, setEditingMarketSentiment] = useState('');
  const [editingTickers, setEditingTickers] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('gleaderPageSize');
    const n = saved ? parseInt(saved) : 10;
    return [3,5,10].includes(n) ? n : 10;
  });
  const [numColumns, setNumColumns] = useState(() => {
    const saved = localStorage.getItem('gleaderColumns');
    const n = saved ? parseInt(saved) : 3;
    return [1,2,3,4,5].includes(n) ? n : 3;
  });
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const [newMarketSentiment, setNewMarketSentiment] = useState('');
  const [newTickers, setNewTickers] = useState('');
  const [, setTotalPages] = useState(1);
  const [, setTotalCount] = useState(0);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const columnWidthStyle = useMemo(() => {
    const safeColumns = Math.max(1, numColumns);
    const percent = 100 / safeColumns;
    return { flex: `0 0 ${percent}%`, maxWidth: `${percent}%` };
  }, [numColumns]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await getGLeaders(1, 1000, '');
      const list = sortThemeItems(resp.data.items || []);
      const orderedList = applyStoredOrder(list);
      setItems(orderedList);
      // Calculate pages based on rows/columns layout
      const count = orderedList.length;
      const notesPerRow = numColumns;
      const totalRows = Math.ceil(count / notesPerRow);
      const pages = Math.max(1, Math.ceil(totalRows / pageSize));
      setTotalPages(pages);
      setTotalCount(count);
      setCurrentPage(1);
      if (!query.trim()) {
        persistOrder(orderedList);
      }
    } catch (e) {
      setError('Failed to load group leaders.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToThemeDetails = (theme) => {
    if (!theme || !theme.name) return;
    navigate(`/gleaders/${encodeURIComponent(theme.name)}`, {
      state: { themeId: theme.id, themeName: theme.name }
    });
  };

  // Initial load handled by query effect (debounced) to avoid double fetch and visual shake.
  // Intentionally mount-only: search filtering below is done client-side against the already
  // -fetched items (see normalizedQuery), so `load` never needs to re-run on query changes.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delay showing skeletons to prevent brief flash and limit count
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowSkeleton(true), 200);
    } else {
      setShowSkeleton(false);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [loading]);

  const handleAdd = async () => {
    if (!newName.trim() || !newMarketSentiment.trim() || !newTickers.trim()) return;
    try {
      setSaving(true);
      await createGLeader(newName.trim(), newMarketSentiment.trim(), newTickers.trim());
      setNewName('');
      setNewMarketSentiment('');
      setNewTickers('');
      await load();
      setShowModal(false);
    } catch (e) {
      setError('Failed to add theme');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setNewName('');
    setNewMarketSentiment('');
    setNewTickers('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setShowModal(false);
    setNewName('');
    setNewMarketSentiment('');
    setNewTickers('');
  };

  const handleEdit = async (id) => {
    try {
      const resp = await updateGLeader(id, {
        name: editingName.trim(),
        market_sentiment: editingMarketSentiment.trim(),
        tickers: editingTickers.trim()
      });
      const updated = resp.data;
      setItems(prev => {
        const next = prev.map(x => x.id === id ? { ...x, ...updated } : x);
        if (!query.trim()) {
          persistOrder(next);
        }
        return next;
      });
      setEditingId(null);
      setEditingName('');
      setEditingMarketSentiment('');
      setEditingTickers('');
    } catch (e) {
      setError('Failed to save theme');
    }
  };

  const handleTogglePinned = async (id, pinned) => {
    try {
      setError('');
      const resp = await updateGLeader(id, { pinned });
      const updated = resp.data;
      setItems(prev => {
        const sorted = reorderAfterPinChange(prev, { ...updated, id });
        if (!query.trim()) {
          persistOrder(sorted);
        }
        return sorted;
      });
      if (pinned) {
        setCurrentPage(1);
      }
    } catch (e) {
      setError('Failed to update stickiness');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteGLeader(id);
      setItems(prev => {
        const next = prev.filter(x => x.id !== id);
        if (!query.trim()) {
          persistOrder(next);
        }
        return next;
      });
      removeFromStoredOrder(id);
    } catch (e) {
      setError('Failed to delete theme');
    }
  };

  // Layout calculations mirroring NotesDetails
  const normalizedQuery = query.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter(item => {
      const nameMatch = item.name?.toLowerCase().includes(normalizedQuery);
      const sentimentMatch = item.market_sentiment?.toLowerCase().includes(normalizedQuery);
      const tickersStr = item.tickers ? String(item.tickers) : '';
      const tickersLower = tickersStr.toLowerCase();
      const tickersMatch = tickersLower.includes(normalizedQuery);

      if (nameMatch || sentimentMatch || tickersMatch) return true;

      // Token match for ticker symbols (e.g., comma/space separated)
      const tokenMatch = tickersStr
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .some(token => token.toLowerCase() === normalizedQuery);

      return tokenMatch;
    });
  }, [items, normalizedQuery]);

  const totalCountLocal = filteredItems.length;
  const notesPerRow = numColumns;
  const totalRows = Math.ceil(totalCountLocal / notesPerRow);
  const pages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPageSafe = Math.min(currentPage, pages);
  const startRow = (currentPageSafe - 1) * pageSize;
  const endRow = Math.min(startRow + pageSize, totalRows);
  const startIndex = startRow * notesPerRow;
  const endIndex = Math.min(startIndex + (pageSize * notesPerRow), totalCountLocal);
  const visible = filteredItems.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (e) => { const v = parseInt(e.target.value); setPageSize(v); localStorage.setItem('gleaderPageSize', String(v)); setCurrentPage(1); };
  const handleColumnsChange = (e) => { const v = parseInt(e.target.value); setNumColumns(v); localStorage.setItem('gleaderColumns', String(v)); };

  const renderPagination = () => {
    const itemsPag = [];
    const maxItems = 7;
    itemsPag.push(<Pagination.Item key={1} active={currentPageSafe === 1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
    let start = Math.max(2, currentPageSafe - 2);
    let end = Math.min(pages - 1, currentPageSafe + 2);
    if (currentPageSafe <= 3) end = Math.min(pages - 1, maxItems - 2);
    else if (currentPageSafe >= pages - 2) start = Math.max(2, pages - maxItems + 2);
    if (start > 2) itemsPag.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    for (let i = start; i <= end; i++) itemsPag.push(<Pagination.Item key={i} active={currentPageSafe === i} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>);
    if (end < pages - 1) itemsPag.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    if (pages > 1) itemsPag.push(<Pagination.Item key={pages} active={currentPageSafe === pages} onClick={() => handlePageChange(pages)}>{pages}</Pagination.Item>);
    return (
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} style={{ width: '80px' }}>
            <option value="3">3</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </Form.Select>
          <span className="ms-3 me-2">Columns:</span>
          <Form.Select size="sm" value={numColumns} onChange={handleColumnsChange} style={{ width: '80px' }}>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </Form.Select>
          <span className="ms-3">
            {totalCountLocal > 0 ? (
              <>Showing rows {startRow + 1} to {endRow} of {totalRows} ({startIndex + 1} to {endIndex} of {totalCountLocal} items)</>
            ) : 'No themes found'}
          </span>
        </div>
        <Pagination size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPageSafe === 1} />
          <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))} disabled={currentPageSafe === 1} />
          {itemsPag}
          <Pagination.Next onClick={() => handlePageChange(Math.min(pages, currentPageSafe + 1))} disabled={currentPageSafe === pages} />
          <Pagination.Last onClick={() => handlePageChange(pages)} disabled={currentPageSafe === pages} />
        </Pagination>
      </div>
    );
  };

  const resetFilters = () => { setQuery(''); setCurrentPage(1); };

  // Function to highlight search terms in text
  const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <mark key={index} className="search-highlight">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleDragStart = (event, id) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(id));
    setDraggingId(id);
  };

  const handleDragEnter = (event, id) => {
    event.preventDefault();
    if (id === draggingId) return;
    setDragOverId(id);
  };

  const handleDragLeave = (event, id) => {
    event.preventDefault();
    if (dragOverId === id) {
      setDragOverId(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = (event, targetId) => {
    event.preventDefault();
    const sourceIdRaw = draggingId ?? event.dataTransfer.getData('text/plain');
    const sourceId = sourceIdRaw ? String(sourceIdRaw) : null;
    const normalizedTargetId = String(targetId);
    if (!sourceId || sourceId === normalizedTargetId) {
      setDragOverId(null);
      return;
    }
    setItems(prev => {
      const next = [...prev];
      const fromIndex = next.findIndex(x => String(x.id) === sourceId);
      const toIndex = next.findIndex(x => String(x.id) === normalizedTargetId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      if (!query.trim()) {
        persistOrder(next);
      }
      return next;
    });
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <Container className="py-4">
      <style>{customStyles}</style>
      <div className="mb-4">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Group className="mb-0 position-relative">
                <Form.Control
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setCurrentPage(1); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }}
                  placeholder={loading ? "Searching..." : "Search leaders..."}
                  size="sm"
                  className="border-0 shadow-sm"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    opacity: loading ? 0.7 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                />
                {loading && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <div className="spinner-border spinner-border-sm text-muted" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                size="sm"
                className="text-nowrap px-3"
                onClick={resetFilters}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Clear
              </Button>
            </Col>
            <Col className="d-flex align-items-end justify-content-end">
              <Button
                variant="primary"
                size="sm"
                className="text-nowrap"
                onClick={handleOpenModal}
              >
                <i className="bi bi-plus-lg me-1"></i>
                GLeader
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div>
        {loading && showSkeleton ? (
          <Row className="g-3">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Col key={idx} style={columnWidthStyle}>
                <div className="border rounded p-2">
                  <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
                  <Placeholder as="div" animation="glow"><Placeholder xs={6} /></Placeholder>
                </div>
              </Col>
            ))}
          </Row>
        ) : loading ? null : (
          visible.length === 0 ? (
            <div className="text-muted">No groups yet.</div>
          ) : (
            <Row className="g-3">
              {visible.map(t => {
                const isEditing = editingId === t.id;
                const dragHandlers = isEditing
                  ? {}
                  : {
                      draggable: true,
                      onDragStart: (event) => handleDragStart(event, t.id),
                      onDragEnter: (event) => handleDragEnter(event, t.id),
                      onDragOver: handleDragOver,
                      onDragLeave: (event) => handleDragLeave(event, t.id),
                      onDrop: (event) => handleDrop(event, t.id),
                      onDragEnd: handleDragEnd,
                    };

                return (
                  <Col key={t.id} style={columnWidthStyle}>
                    <div
                      className={`theme-card border rounded p-3 h-100 d-flex flex-column${!isEditing ? ' theme-card-draggable' : ''}${draggingId === t.id ? ' theme-card-dragging' : ''}${dragOverId === t.id ? ' theme-card-drag-over' : ''}${t.pinned ? ' theme-card-pinned' : ''}`}
                      style={{ whiteSpace: 'pre-wrap' }}
                      role="listitem"
                      aria-grabbed={!isEditing && draggingId === t.id}
                      {...dragHandlers}
                    > 
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fw-bold fs-5 text-truncate d-flex align-items-center" title={t.name}>
                        <Button
                          variant="link"
                          className="p-0 me-1 flex-grow-1 text-truncate text-decoration-none"
                          style={{ color: 'inherit' }}
                          onClick={() => navigateToThemeDetails(t)}
                        >
                          <span className="flex-grow-1 text-truncate d-block text-start">
                            {highlightSearchTerms(t.name, query)}
                          </span>
                        </Button>
                        {t.pinned && (
                          <i
                            className="bi bi-pin-angle-fill text-warning ms-1"
                            aria-label="Pinned group"
                            title="Pinned group"
                          ></i>
                        )}
                      </div>
                      <div className="d-flex gap-1 ms-2">
                        <Button
                          variant={t.pinned ? 'warning' : 'outline-warning'}
                          size="sm"
                          className="py-0 px-1"
                          title={t.pinned ? 'Unstick from top' : 'Stick to top'}
                          aria-label={t.pinned ? 'Unstick from top' : 'Stick to top'}
                          aria-pressed={t.pinned}
                          type="button"
                          onClick={() => handleTogglePinned(t.id, !t.pinned)}
                        >
                          <i className={`bi ${t.pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                        </Button>
                        {isEditing ? null : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-0 px-1"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() => {
                              setEditingId(t.id);
                              setEditingName(t.name);
                              setEditingMarketSentiment(t.market_sentiment);
                              setEditingTickers(t.tickers);
                            }}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 px-1"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => handleDelete(t.id)}
                        >
                          <i className="bi bi-trash" style={{ color: '#dc3545' }}></i>
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex-grow-1">
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="text"
                            className="mb-2"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            placeholder="Name"
                          />
                          <div className="mb-2">
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editingMarketSentiment}
                              onChange={(e) => setEditingMarketSentiment(e.target.value)}
                              placeholder="Market Sentiment"
                            />
                          </div>
                          <div>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editingTickers}
                              onChange={(e) => setEditingTickers(e.target.value)}
                              placeholder="Tickers"
                            />
                          </div>
                          <div className="mt-2 d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(t.id)}
                              disabled={!editingName.trim() || !editingMarketSentiment.trim() || !editingTickers.trim()}
                            >
                              Save
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditingName('');
                                setEditingMarketSentiment('');
                                setEditingTickers('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-muted small">{formatDateTime(t.updated_at || t.created_at)}</div>
                          <div className="mt-2">
                            <div className="fw-semibold mb-1" style={{ fontSize: '1.05rem' }}>Market Sentiment:</div>
                            <div className="mb-2">{highlightSearchTerms(t.market_sentiment, query)}</div>
                            <div className="fw-semibold mb-1" style={{ fontSize: '1.05rem' }}>Tickers:</div>
                            <div>{highlightSearchTerms(t.tickers, query)}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              );
              })}
            </Row>
          )
        )}
      </div>

      {renderPagination()}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
        dialogClassName="modal-600w"
      >
        <Form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>New GLeader</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="themeName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name"
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="themeMarketSentiment">
              <Form.Label>Market Sentiment</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newMarketSentiment}
                onChange={(e) => setNewMarketSentiment(e.target.value)}
                placeholder="Market Sentiment"
              />
            </Form.Group>
            <Form.Group controlId="themeTickers">
              <Form.Label>Tickers</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newTickers}
                onChange={(e) => setNewTickers(e.target.value)}
                placeholder="Tickers"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving || !newName.trim() || !newMarketSentiment.trim() || !newTickers.trim()}
            >
              {saving ? 'Saving...' : 'Save GLeader'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default GLeaders; 
